import { Component, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import * as _ from 'lodash';
import { MenuItem, MessageService } from "primeng/api";
import { BreadcrumbModule } from "primeng/breadcrumb";
import { ButtonModule } from "primeng/button";
import { DialogModule } from "primeng/dialog";
import { TabViewModule } from "primeng/tabview";
import { AppSettings } from "../../../shared/app.settings";
import { MiFormComponent } from "../../../shared/components/mi-form/mi-form.component";
import type { Country } from "../../../shared/models/country";
import type { Language } from "../../../shared/models/language";
import { CommonBindingDataService } from "../../../shared/services/common-binding-data.service";
import { EntityService } from "../../../shared/services/entity.service";
import type { AttributeData } from "app/modules/shared/models/attribute.models";

import { AccessProviderDirective } from "app/modules/shared/directives/access-provider.directive";
import { ConfigService } from "app/modules/shared/services/config.service";
import dayjs from "dayjs";
import type { EntityResponse } from "app/modules/shared/models/entityResponse.models";

interface RelatedDataItem {
    entityCode: string;
    entityId: string;
    actionStatus: string;
    [key: string]: unknown;
}

interface OrganizedData {
    [key: string]: Record<string, unknown>[];
}

interface TabChangeEvent {
    index: number;
    originalEvent: {
        target: {
            innerText: string;
        };
    };
}

@Component({
    selector: 'app-add-sponsors',
    standalone: true,
    imports: [
        BreadcrumbModule,
        TabViewModule,
        MiFormComponent,
        DialogModule,
        ButtonModule,
        TranslateModule,
        AccessProviderDirective
    ],
    templateUrl: './add-sponsor.component.html',
    styleUrl: './add-sponsor.component.scss'
})
export class AddSponsorComponent implements OnInit {

    @ViewChild(MiFormComponent) miFormComponent: MiFormComponent;
    activeIndex: number = 0;
    data: AttributeData;
    sponsorAttributeData: AttributeData;
    routePath: MenuItem[] = [];
    attributeLabels: Record<string, string> = {};
    moduleName: string;
    country: Country;
    language: Language;
    relation: string = "oneToOne";
    cols: Array<{ field: string; header: string }> = [];
    sponsorAttributeLength: number;
    sponsorId: string;
    sponsorIdForDocument: string;
    sponsorDocumentId: string;
    driverRelation: { id: string; name: string; entityRelationshipConfigId?: string };
    attributeValues: Record<string, unknown> = {};
    previousBtnLabel: string;
    label: string;
    initialLabel: string = this.cs.getLabel("lbl_details");
    saveSponsorBtnLabel: string;
    isFirstTabSaved: boolean = false;
    isNew: boolean = true;
    prefixToAttachForButtonIdToUniquelyIdentifyButton = AppSettings.BUTTON_PREFIX;
    tabCode: string;
    tabIndex: number;

    constructor(
        private entityService: EntityService,
        private cs: CommonBindingDataService,
        private messageService: MessageService,
        private router: Router,
        private route: ActivatedRoute,
        private configService: ConfigService
    ) { }

    ngOnInit() {
        this.route.queryParams.subscribe((params) => {
            this.sponsorId = params.id;
        })
        this.retrieveSponsorId();
        this.initializeLabels();
        this.initializeSponsorService();
        this.loadSponsorData();
        this.setRoutePath('details');
        this.isFirstTabSaved = false;
        this.activeIndex = 0;
    }

    private initializeLabels() {
        this.previousBtnLabel = this.cs.getLabel("lbl_previous");
        this.saveSponsorBtnLabel = this.sponsorId ?
            this.cs.getLabel("sponsor.lbl_update_sponsor") :
            this.cs.getLabel("sponsor.lbl_save_sponsor");
    }

    private retrieveSponsorId() {
        if (this.sponsorId) {
            this.sponsorIdForDocument = this.sponsorId;
        }
    }

    private initializeSponsorService() {
        this.moduleName = AppSettings.ENTITY_TYPE.SPONSOR;
        this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
        this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
        this.entityService.setRelatedData([]);
    }

    private loadSponsorData() {
        if (this.sponsorId) {
            this.entityService
                .getEntity(this.sponsorId, AppSettings.ENTITY_CODE.SPONSOR, AppSettings.VIEW_CODE.ADD_EDIT_VIEW)
                .subscribe((res: EntityResponse) => {
                    if (res) {
                        this.processSponsorResponse(res);
                        this.isNew = false;
                        const currentTab = this.sponsorAttributeData?.tabs[this.activeIndex];
                        if (currentTab) {
                            this.setRoutePath(currentTab.tabCode);
                        }
                    }
                });
        } else {
            this.getAttributes();
        }
    }

    private processSponsorResponse(data: EntityResponse) {
        this.attributeValues = this.cs.getOrgAttributeValues(data) as unknown as Record<string, unknown>;
        this.normalizeManufacturingYear();
        this.processRelatedData(data.relatedData as unknown as RelatedDataItem[]);
        this.getAttributes();
    }

    private normalizeManufacturingYear() {
        const yearOfMnfgAttributeCode = "year_of_manufacturing";
        if (this.attributeValues[yearOfMnfgAttributeCode]) {
            this.attributeValues[yearOfMnfgAttributeCode] = new Date(
                this.attributeValues[yearOfMnfgAttributeCode] as number, 0
            );
        }
    }

    private processRelatedData(relatedData: RelatedDataItem[]) {
        if (relatedData.length > 0) {
            const organizedData: OrganizedData = {};
            relatedData.forEach(item => {
                if (item.actionStatus === "DELETED") {
                    return;
                }
                const entityCode = item.entityCode;
                if (!organizedData[entityCode]) {
                    organizedData[entityCode] = [];
                }
                const entityData = this.cs.getOrgAttributeValues(item) as unknown as Record<string, unknown>;
                entityData.entityId = item.entityId;
                organizedData[entityCode].push(entityData);
            });
            this.entityService.setRelatedData(organizedData);
        }
    }

    onTabChange(event: TabChangeEvent) {
        const currentTab = this.sponsorAttributeData.tabs[this.activeIndex];
        if (currentTab) {
            this.setRoutePath(currentTab.tabCode);
        }
        if (event.index === 0) {
            this.isFirstTabSaved = false;
        }
        if (!this.isFirstTabSaved && event.index > 0) {
            this.activeIndex = 0;
            this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: 'Please save details in first tab before proceeding'
            });
            return;
        }

        this.label = event.originalEvent.target.innerText.trim();
    }

    getAttributes() {
        const entityType = AppSettings.ENTITY_TYPE.SPONSOR;
        const viewCode = AppSettings.VIEW_CODE.ADD_EDIT_VIEW;
        this.entityService.getAttributeDefinition(entityType, viewCode).subscribe(res => {
            if (!res) return;
            this.setSponsorView(res);
            const currentTab = this.sponsorAttributeData.tabs[this.activeIndex];
            if (currentTab) {
                this.setRoutePath(currentTab.tabCode);
            }
        });
    }

    setSponsorView(res: AttributeData) {
        this.attributeLabels = this.cs.getAttributeLabels(res) as unknown as Record<string, string>;
        this.sponsorAttributeData = this.cs.getOrganizedAttribute(res);
        this.sponsorAttributeData.tabs = this.cs.setDependsOnAttributeCode(this.sponsorAttributeData?.tabs);
        this.tabIndex = this.sponsorAttributeData.tabs.length;
        this.sponsorAttributeData.tabs.forEach(tab => {
            this.setRoutePath(tab.tabCode);
        });
        this.setDateFieldValidations();
        this.sponsorAttributeLength = this.sponsorAttributeData.tabs.length;
        this.driverRelation = _.find(res?.relations, { otherEntityCode: 'driver', relation: "oneToMany" });
    }

    private setDateFieldValidation(tabCode, groupCode, attributeCode, isMax) {
        const tab = this.sponsorAttributeData.tabs.find(ele => ele.tabCode === tabCode);
        const group = tab?.groups.find(ele => ele.code === groupCode);
        const field = group?.fields.find(ele => ele.attributeCode === attributeCode);

        if (field) {
            field.validation[isMax ? 'max' : 'min'] = new Date().getTime();
        }
    }

    private setDateFieldValidations() {
        this.setDateFieldValidation('documents', 'documents', AppSettings.DATE_ATTRIBUTE_IDS.VEHICLE_DOCUMENT_ISSUED_DATE, true);
        this.setDateFieldValidation('documents', 'documents', AppSettings.DATE_ATTRIBUTE_IDS.VEHICLE_DOCUMENT_EXPIRY_DATE, false);
        // this.setDateFieldValidation('details', 'additional_details', AppSettings.DATE_ATTRIBUTE_IDS.VEHICLE_DETAIL_YEAR_OF_MFG, true);
    }

    setRoutePath(tabCode: any) {
        const routeLabel = this.sponsorId ? this.cs.getLabel("Edit Sponsor") : this.cs.getLabel("Add New Sponsor");
        if (!tabCode && this.sponsorAttributeData?.tabs?.length > 0) {
            tabCode = this.sponsorAttributeData.tabs[0].tabCode;
        }

        this.routePath = [
            {
                label: `${routeLabel}`,
                routerLink: '/app/sponsors',
                command: () => {
                    if (window.history.length > 1) {
                        window.history.go(-1);
                    } else {
                        this.router.navigate(["/app/sponsors"]);
                    }
                },
                icon: "pi pi-arrow-left",
                iconStyle: { "font-weight": "bold", "margin-right": "10px" },
            }
        ];
        if (tabCode) {
            this.routePath.push({
                label: `${tabCode}`,
                styleClass: 'breadcrumb-child forward-slash breadcrumb-text',
                style: { 'display': 'flex', 'top': '2px', 'position': 'relative' }
            });
        }
    }

    onSaveSponsor(event) {
        const sponsorData = {
            forTenantCode: this.configService.getForTenantCode(),
            entityCode: this.moduleName,
            countryCode: this.country[0].countryCode,
            languageCode: this.language[0].langCode,
            data: [],
            relationshipData: []
        };

        const vData = event;

        const dateIds = ["year_of_manufacturing", "issued_date", "expiry_date", "in_service_date", "out_of_service_date"];
        for (const [key, value] of Object.entries(vData)) {
            let attributeValue = <any>value;
            if (dateIds.includes(key) && key !== "year_of_manufacturing") {
                attributeValue = new Date(<string>value).getTime();
            } else if (key == "year_of_manufacturing") {
                attributeValue = Number(dayjs(<number>value).format('YYYY'))
            } else if (key === 'driver') {
                attributeValue?.map(driverId => {
                    sponsorData?.relationshipData?.push({
                        entityRelationshipConfigId: this.driverRelation.entityRelationshipConfigId,
                        otherEntityId: driverId
                    });
                })
            }

            const obj = typeof attributeValue;
            if (obj == "object") {
                if (key === 'driver') {
                    sponsorData.data.push({
                        attributeCode: key,
                        attributeValue: attributeValue ? attributeValue : []
                    });
                } else if (key === 'labels') {
                    if (!attributeValue || attributeValue.length === 0) {
                        sponsorData.data.push({
                            attributeCode: key,
                            attributeValue: [],
                        });
                    } else {
                        sponsorData.data.push({
                            attributeCode: key,
                            attributeValue,
                        });
                    }
                } else if (key === "region_geofence") {
                    if (attributeValue && attributeValue.length > 0) {
                        const valueToPush = Array.isArray(attributeValue) ? attributeValue.join(", ") : attributeValue;
                        sponsorData.data.push({
                            attributeCode: key,
                            attributeValue: valueToPush,
                        });
                    }
                } else if (key === "features") {
                    if (attributeValue && attributeValue.length > 0) {
                        sponsorData.data.push({
                            attributeCode: key,
                            attributeValue,
                        });
                    } else if (!attributeValue || attributeValue.length === 0) {
                        sponsorData.data.push({
                            attributeCode: key,
                            attributeValue: [],
                        });
                    }
                } else if (attributeValue?.length > 0) {
                    sponsorData.data.push({
                        attributeCode: key,
                        attributeValue,
                    });
                }
            } else {
                sponsorData.data.push({
                    attributeCode: key,
                    attributeValue,
                });
            }
        }

        const entitiesData = {
            countryCode: this.country[0].countryCode,
            tenantCode: this.configService.getForTenantCode(),
        };

        if (!this.sponsorId) {
            this.entityService
                .createEntities(this.moduleName, entitiesData)
                .subscribe((res: EntityResponse) => {
                    this.sponsorId = res.entityId;
                    if (this.sponsorId) {
                        this.sponsorIdForDocument = this.sponsorId;
                    }
                    this.saveSponsorData(sponsorData, true);
                });
        } else {
            this.saveSponsorData(sponsorData, false);
        }
    }

    saveSponsorData(sponsorData, isNew: boolean) {
        this.isNew = isNew;
        sponsorData.data = this.cs.mapAttributeIds(sponsorData.data, this.sponsorAttributeData.tabs)
        this.entityService
            .saveAttributeData(sponsorData.entityCode, this.sponsorId, sponsorData).subscribe({
                next: () => {
                    if (this.isNew) {
                        this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel('sponsor.lbl_sponsor_added_successfully') });
                    } else {
                        this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: this.cs.getLabel('sponsor.lbl_sponsor_updated_successfully') });
                    }
                    this.isFirstTabSaved = true;

                    setTimeout(() => {
                        if (this.isLastIndex()) {
                            this.redirectToListPage();
                        } else {
                            this.onNextClick();
                            this.label = this.cs.getLabel("lbl_details");
                        }
                    }, 500);
                },
                error: () => {
                    if (this.activeIndex === 0) {
                        this.isFirstTabSaved = false;
                    }
                    if (isNew) {
                        this.cs.onDisplayMessage(this.data, 'sponsor', AppSettings.MSG_ACTION.ADD, 'error');
                    } else {
                        this.cs.onDisplayMessage(this.data, 'sponsor', AppSettings.MSG_ACTION.UPDATE, 'error');
                    }
                }
            });
    }

    onPreviousClick() {
        if (this.activeIndex > 0) {
            this.activeIndex--;
        }
        if (this.activeIndex === 0) {
            this.isFirstTabSaved = false;
        }
        const currentTab = this.sponsorAttributeData.tabs[this.activeIndex];
        if (currentTab) {
            this.setRoutePath(currentTab.tabCode);
        }
    }

    onNextBtnClick() {
        if (this.sponsorAttributeData?.tabs && this.sponsorAttributeData.tabs.length > 0) {
            const currentTab = this.sponsorAttributeData.tabs[this.activeIndex];
            this.setRoutePath(currentTab.tabCode);
            if (currentTab) {
                const btn = document.getElementById(`0_${currentTab.tabCode}`);
                if (btn) {
                    btn.click();
                }
            }
        }
    }

    onNextClick() {
        if (this.activeIndex < this.sponsorAttributeLength - 1) {
            this.activeIndex++;
        }
        const currentTab = this.sponsorAttributeData.tabs[this.activeIndex];
        if (currentTab) {
            this.setRoutePath(currentTab.tabCode);
        }
    }

    getLastIndex(): number {
        return this.sponsorAttributeLength - 1;
    }

    isLastIndex(): boolean {
        return this.activeIndex === this.getLastIndex();
    }

    onCancel() {
        this.isFirstTabSaved = false;
        this.miFormComponent.resetForm();
        this.redirectToListPage();
    }

    redirectToListPage() {
        if (window.history.length > 1) {
            window.history.go(-1);
        } else {
            this.router.navigate(["/app/sponsors"]);
        }
    }
}
